import { Link } from 'react-router-dom'
import type { ConversationListItem as ConvItem } from '@/hooks/useConversations'

type Props = {
  conversation: ConvItem
}

export function ConversationListItem({ conversation: c }: Props) {
  const displayName =
    c.other_user_first_name && c.other_user_last_name
      ? `${c.other_user_first_name} ${c.other_user_last_name}`
      : c.other_user_username

  const initials = getInitials(
    c.other_user_first_name,
    c.other_user_last_name,
    c.other_user_username
  )

  return (
    <Link
      to={`/chat/${c.id}`}
      className="flex items-center gap-3 px-4 py-3 hover:bg-accent/50 transition-colors"
    >
      {/* Avatar */}
      <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold shrink-0">
        {initials}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className="font-medium text-sm truncate">{displayName}</span>
          {c.last_message_at && (
            <span className="text-[10px] text-muted-foreground shrink-0 ml-2">
              {formatRelativeTime(c.last_message_at)}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between mt-0.5">
          <p className="text-xs text-muted-foreground truncate">
            {c.last_message_preview ?? 'No messages yet'}
          </p>
          {c.unread_count > 0 && (
            <span className="ml-2 min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center justify-center shrink-0">
              {c.unread_count > 99 ? '99+' : c.unread_count}
            </span>
          )}
        </div>
      </div>
    </Link>
  )
}

function getInitials(
  firstName: string,
  lastName: string,
  username: string
): string {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase()
  }
  return (username[0] ?? '?').toUpperCase()
}

function formatRelativeTime(iso: string): string {
  const date = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'now'
  if (diffMins < 60) return `${diffMins}m`
  if (diffHours < 24) return `${diffHours}h`
  if (diffDays < 7) return `${diffDays}d`
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
